import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import { SortDirection } from 'mapistry-shared';
import React, { AriaAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { VisuallyHidden } from '../VisuallyHidden';
import { HeaderLabelWithSorting } from './HeaderLabelWithSorting';
import { StyledTableCell } from './TableCell';
import { BaseTableRow, SortState, TableColumn } from './types';

function getAriaSort(
  isSortedColumn: boolean,
  direction: SortDirection,
): AriaAttributes['aria-sort'] {
  if (!isSortedColumn) {
    return 'none';
  }
  return direction === SortDirection.ASC ? 'ascending' : 'descending';
}

const TableHead = styled(MuiTableHead)`
  text-transform: none;
  background-color: ${({ theme }) => theme.colors.blue04};
`;

const TableHeaderCell = styled(StyledTableCell)`
  padding-top: 0.3rem;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.gray333};
  vertical-align: middle;
`;

// Remove unnecessary padding to the collapse arrow from the cell next to it
const CollapsibleSectionTableHeaderCell = styled(TableHeaderCell)`
  width: 1rem;

  &.MuiTableCell-paddingNone:first-of-type {
    padding-left: 0;
  }

  & + .MuiTableCell-root {
    padding-left: 0.25rem;
  }
`;

const CheckboxTableHeaderCell = styled(TableHeaderCell)`
  width: 2.625rem;
`;

export interface SharedBaseTableProps<RowEntity extends BaseTableRow> {
  ariaLabel: string;
  canRowsBeSelected?: boolean;
  columns: TableColumn<RowEntity>[];
  isCompact?: boolean;
  onSortByColumn?: (column: TableColumn<RowEntity>) => void;
  sortState?: SortState;
}

type BaseTableProps<RowEntity extends BaseTableRow> = {
  children: ReactNode; // Row or row-like lists to populate the table
  hasCollapsibleSections?: boolean;
} & SharedBaseTableProps<RowEntity>;

export function BaseTable<RowEntity extends BaseTableRow>({
  ariaLabel,
  canRowsBeSelected = false,
  children,
  columns,
  hasCollapsibleSections = false,
  isCompact,
  onSortByColumn,
  sortState,
}: BaseTableProps<RowEntity>) {
  return (
    <MuiTable aria-label={ariaLabel} stickyHeader>
      <TableHead>
        <MuiTableRow>
          {hasCollapsibleSections && (
            <CollapsibleSectionTableHeaderCell padding="none">
              <VisuallyHidden>Expand Section</VisuallyHidden>
            </CollapsibleSectionTableHeaderCell>
          )}
          {canRowsBeSelected && (
            <CheckboxTableHeaderCell padding="none" $isCompact={isCompact}>
              <VisuallyHidden>Selection</VisuallyHidden>
            </CheckboxTableHeaderCell>
          )}
          {columns.map((col) => (
            <TableHeaderCell
              $isCompact={isCompact}
              align={col.align}
              key={col.id}
              width={col.width}
              padding={col.padding}
              aria-sort={
                onSortByColumn && col.isSortable && sortState?.direction
                  ? getAriaSort(
                      col.id === sortState.columnId,
                      sortState.direction,
                    )
                  : undefined
              }
            >
              {onSortByColumn && col.isSortable ? (
                <HeaderLabelWithSorting<RowEntity>
                  column={col}
                  onSortByColumn={onSortByColumn}
                  sortState={sortState}
                />
              ) : (
                col.header
              )}
            </TableHeaderCell>
          ))}
        </MuiTableRow>
      </TableHead>
      <MuiTableBody>{children}</MuiTableBody>
    </MuiTable>
  );
}
